import type {
  CompanyQualificationSheet,
  QualificationStatus,
  QualificationThemeKey,
} from './company-qualification.ts';

/**
 * The restitution of a company qualification (08/10/2026), written from the
 * sheet by a separate writing step once a step closes. Three levels, the way
 * audits and analyst reports are read: the answer on one screen (brief), one
 * page per theme with a title that concludes and the visual that fits the theme,
 * then the sheet's lines as evidence. Every element cites the line ids that
 * support it; its status is computed from them, never written by the model.
 */

export const VISUAL_KINDS = ['funnel', 'table', 'steps', 'scorecard', 'cards'] as const;
export type VisualKind = typeof VISUAL_KINDS[number];

/** One visual per theme, so a theme always reads the same way; table columns are fixed. */
export const THEME_VISUALS: Record<QualificationThemeKey, { kind: VisualKind; columns?: readonly string[] }> = {
  company: { kind: 'table', columns: ['Offre', 'Prix', 'Pour qui', 'Preuve'] },
  icp: { kind: 'cards' },
  competition: { kind: 'table', columns: ['Alternative', 'Ce qu’elle fait', 'Prix', 'Face à vous'] },
  tools: { kind: 'scorecard' },
  workflows: { kind: 'steps' },
  pains: { kind: 'table', columns: ['Goulot', 'Aujourd’hui', 'Cause', 'Coût'] },
  objectives: { kind: 'funnel' },
  team: { kind: 'table', columns: ['Personne ou rôle', 'Temps disponible', 'Avec Domino', 'Ce qui freine'] },
};

export const SCORE_LEVELS = ['good', 'watch', 'bad'] as const;
export type ScoreLevel = typeof SCORE_LEVELS[number];

export interface FunnelStage {
  label: string;
  /** Volume with its unit, e.g. « 35 emails par semaine ». */
  value: string;
  /** Rate from the previous stage, e.g. « 3 % »; empty for the first stage. */
  rate: string;
  /** The rate is an assumption, not a measure. */
  assumed: boolean;
  evidence: string[];
}

export type ThemeVisual =
  | { kind: 'funnel'; stages: FunnelStage[]; bottleneck: string }
  | { kind: 'table'; rows: Array<{ cells: string[]; highlight: boolean; evidence: string[] }> }
  | { kind: 'steps'; steps: Array<{ label: string; who: string; tool: string; time: string; issue: string; evidence: string[] }> }
  | { kind: 'scorecard'; indicators: Array<{ label: string; value: string; level: ScoreLevel; note: string; evidence: string[] }> }
  | { kind: 'cards'; cards: Array<{ title: string; facts: Array<{ label: string; value: string }>; evidence: string[] }> };

export interface ThemePage {
  /** A sentence that concludes (an action title), not a topic. */
  title: string;
  /** What it changes for the proposal, one or two sentences. */
  takeaway: string;
  visual: ThemeVisual;
  evidence: string[];
  writtenAt: string;
  /** Fingerprint of the theme's lines when written: tells when the page is out of date. */
  basis: string;
}

export interface BriefFinding { title: string; detail: string; evidence: string[] }
export interface BriefStep { text: string; when: string; proposalId?: string }
export interface BriefDecision {
  text: string;
  evidence: string[];
  /** The manager's answer, also written into the sheet as a validated line. */
  answer?: string;
  answeredAt?: string;
}

export interface QualificationBrief {
  /** The diagnosis in one sentence. */
  headline: string;
  findings: BriefFinding[];
  steps: BriefStep[];
  decisions: BriefDecision[];
  writtenAt: string;
  basis: string;
}

export interface QualificationRestitution {
  brief?: QualificationBrief;
  pages: Partial<Record<QualificationThemeKey, ThemePage>>;
}

/** Weakest first: an element is only as solid as its weakest evidence. */
const STATUS_STRENGTH: Record<QualificationStatus, number> = { gap: 0, hypothesis: 1, observed: 2, validated: 3 };

/**
 * The status of an element from the lines it cites: the weakest of its facts
 * (validated, observed, hypothesis). A « gap » line says what is unknown; it
 * makes an element « unknown » only when nothing else supports it. Null when
 * none of the cited lines exists.
 */
export const evidenceStatus = (
  linesById: ReadonlyMap<string, { status: QualificationStatus }>,
  ids: readonly string[],
): QualificationStatus | null => {
  let weakest: QualificationStatus | null = null;
  let gap = false;
  for (const id of ids) {
    const line = linesById.get(id);
    if (!line) continue;
    if (line.status === 'gap') { gap = true; continue; }
    if (weakest === null || STATUS_STRENGTH[line.status] < STATUS_STRENGTH[weakest]) weakest = line.status;
  }
  return weakest ?? (gap ? 'gap' : null);
};

/** FNV-1a: a short, stable fingerprint, the same in the browser and on the server. */
const fingerprint = (value: unknown): string => {
  const input = JSON.stringify(value);
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
};

const withoutDates = <T extends { date?: string }>(items: readonly T[]) => items.map(({ date: _date, ...rest }) => rest);

/** What the brief stands on: every line, the hypotheses and the proposal. */
export const briefBasis = (sheet: CompanyQualificationSheet): string => fingerprint({
  sections: Object.fromEntries(Object.entries(sheet.sections).map(([key, items]) => [key, withoutDates(items)])),
  hypotheses: withoutDates(sheet.hypotheses),
  proposal: sheet.proposal,
});

/** What a theme page stands on: the theme's lines and the contradictions. */
export const pageBasis = (sheet: CompanyQualificationSheet, sections: readonly string[]): string => fingerprint(
  [...sections, 'contradictions'].map((key) => withoutDates(sheet.sections[key as keyof CompanyQualificationSheet['sections']] ?? [])),
);

// ---------- Tolerant reading: an invalid element is dropped, never guessed. ----------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value: unknown, max = 600): string =>
  typeof value === 'string' ? value.trim().slice(0, max) : '';
const ids = (value: unknown): string[] =>
  Array.isArray(value) ? [...new Set(value.filter((entry): entry is string => typeof entry === 'string' && /^[a-z][a-z0-9_-]{0,63}$/.test(entry)))] : [];
const each = <T>(value: unknown, parse: (entry: Record<string, unknown>) => T | null): T[] =>
  Array.isArray(value) ? value.flatMap((entry) => (isRecord(entry) ? parse(entry) : null) ?? []) : [];

/** Reads a visual of the kind its theme requires; null when nothing usable is left. */
export const parseThemeVisual = (theme: QualificationThemeKey, value: unknown): ThemeVisual | null => {
  if (!isRecord(value)) return null;
  const { kind, columns } = THEME_VISUALS[theme];
  if (kind === 'funnel') {
    const stages = each(value.stages, (stage) => {
      const label = text(stage.label, 120); const volume = text(stage.value, 120);
      return label && volume ? { label, value: volume, rate: text(stage.rate, 60), assumed: stage.assumed === true, evidence: ids(stage.evidence) } : null;
    });
    return stages.length ? { kind, stages, bottleneck: text(value.bottleneck) } : null;
  }
  if (kind === 'table') {
    const width = columns?.length ?? 0;
    const header = (columns ?? []).map((column) => column.toLowerCase());
    const rows = each(value.rows, (row) => {
      const cells = Array.isArray(row.cells) ? row.cells.map((cell) => text(cell, 400)) : [];
      // The page draws the column headers: a row repeating them is dropped.
      const repeatsHeader = cells.every((cell, index) => cell.toLowerCase() === header[index]);
      return cells.length === width && cells[0] && !repeatsHeader ? { cells, highlight: row.highlight === true, evidence: ids(row.evidence) } : null;
    });
    return rows.length ? { kind, rows } : null;
  }
  if (kind === 'steps') {
    const steps = each(value.steps, (step) => {
      const label = text(step.label, 160);
      return label ? { label, who: text(step.who, 160), tool: text(step.tool, 160), time: text(step.time, 120), issue: text(step.issue, 400), evidence: ids(step.evidence) } : null;
    });
    return steps.length ? { kind, steps } : null;
  }
  if (kind === 'scorecard') {
    const indicators = each(value.indicators, (indicator) => {
      const label = text(indicator.label, 160); const shown = text(indicator.value, 160);
      const level = SCORE_LEVELS.find((entry) => entry === indicator.level);
      return label && shown && level ? { label, value: shown, level, note: text(indicator.note, 400), evidence: ids(indicator.evidence) } : null;
    });
    return indicators.length ? { kind, indicators } : null;
  }
  const cards = each(value.cards, (card) => {
    const title = text(card.title, 160);
    const facts = each(card.facts, (fact) => {
      const label = text(fact.label, 80); const shown = text(fact.value, 400);
      return label && shown ? { label, value: shown } : null;
    });
    return title && facts.length ? { title, facts, evidence: ids(card.evidence) } : null;
  });
  return cards.length ? { kind: 'cards', cards } : null;
};

export const parseThemePage = (theme: QualificationThemeKey, value: unknown): Omit<ThemePage, 'writtenAt' | 'basis'> | null => {
  if (!isRecord(value)) return null;
  const title = text(value.title, 300); const visual = parseThemeVisual(theme, value.visual);
  return title && visual ? { title, takeaway: text(value.takeaway, 1200), visual, evidence: ids(value.evidence) } : null;
};

export const parseBrief = (value: unknown): Omit<QualificationBrief, 'writtenAt' | 'basis'> | null => {
  if (!isRecord(value)) return null;
  const headline = text(value.headline, 400);
  const findings = each(value.findings, (finding) => {
    const title = text(finding.title, 300);
    return title ? { title, detail: text(finding.detail, 400), evidence: ids(finding.evidence) } : null;
  });
  const steps = each(value.steps, (step) => {
    const stepText = text(step.text, 400); const proposalId = ids([step.proposalId])[0];
    return stepText ? { text: stepText, when: text(step.when, 80), ...(proposalId ? { proposalId } : {}) } : null;
  });
  const decisions = each(value.decisions, (decision) => {
    const decisionText = text(decision.text, 400); const answer = text(decision.answer, 1000);
    const answeredAt = typeof decision.answeredAt === 'string' && DATE.test(decision.answeredAt) ? decision.answeredAt : '';
    return decisionText ? { text: decisionText, evidence: ids(decision.evidence), ...(answer ? { answer, ...(answeredAt ? { answeredAt } : {}) } : {}) } : null;
  });
  return headline && findings.length ? { headline, findings, steps, decisions } : null;
};

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const stamp = (value: Record<string, unknown>) => ({
  writtenAt: typeof value.writtenAt === 'string' && DATE.test(value.writtenAt) ? value.writtenAt : '',
  basis: text(value.basis, 16),
});

/** Reads a stored restitution. */
export const parseRestitution = (value: unknown): QualificationRestitution => {
  const restitution: QualificationRestitution = { pages: {} };
  if (!isRecord(value)) return restitution;
  if (isRecord(value.brief)) {
    const brief = parseBrief(value.brief);
    if (brief) restitution.brief = { ...brief, ...stamp(value.brief) };
  }
  const pages = isRecord(value.pages) ? value.pages : {};
  for (const theme of Object.keys(THEME_VISUALS) as QualificationThemeKey[]) {
    const raw = pages[theme];
    const page = isRecord(raw) ? parseThemePage(theme, raw) : null;
    if (page && isRecord(raw)) restitution.pages[theme] = { ...page, ...stamp(raw) };
  }
  return restitution;
};
